'use client';

import { useTranslations } from 'next-intl';
import { BottomBar } from './BottomBar';
import { Capabilities } from 'components/ring-air/Capabilities';
import { Contents } from 'components/ring-air/Contents';
import { ProductWizard } from 'components/shared/ProductWizard';
import { RareSelectionContextProvider } from 'components/rare/RareSelectionContext';
import { RareSecondaryNavbar } from 'components/rare/RareSecondaryNavbar';
import { SolidBgTextSection } from 'components/ring-air/SolidBgTextSection';
import { FAQSection } from 'components/shared/FAQSection';
import { TechnicalSpecs } from 'components/technicalSpecs/TechnicalSpecs';
import { ShippingEstimateObject } from 'components/rare/utils/types';
import { useEffect } from 'react';
import { RingSizes } from 'content/products/ring';
import { RareColors } from 'components/rare/RareSelectionContext';
// import { ringBuyPageFaqData } from 'content/ringFaqData';
import { useGlobalUI } from 'contexts/GlobalUIContext';
import { ShopifyStore } from 'lib/commerce/shopify/shopifyHelper';
import { ExpertSection } from 'components/rare/ExpertSection';
import { default as RareProductSelectionSection } from 'components/rare/RareProductSelectionSection';
import { RARE_CONTENTS_DATA } from 'content/rare';
import { Currency } from 'utils/Currency';
import { useRouter, useSearchParams } from 'next/navigation';
import { ALLOWED_RARE_COUNTRIES } from 'components/rare/constants';
import { useRegionLocale } from 'contexts/RegionLocaleContext';
import { faqData } from './FAQSection';
import { ImageModal } from 'components/ring-air/ProductSelectionSection/ImageModal';
import { HsaFsaOffer } from 'components/ring-air/secondaryOffers';
import { ColorCatalogueSection } from './ColorCatalogueSection';
import styles from './RareBuy.module.css';

const PageWrapper = ({ children }: { children?: React.ReactNode }) => (
  <div className={styles.pageWrapper}>{children}</div>
);

// Get country-specific secondary offers based on store - replicates ring/buy pages behavior
const getSecondaryOfferData = (store: ShopifyStore) => {
  // Base Heroes offer that all stores get
  // const heroesOffer = {
  //   title: <b>Special pricing for the Heroes Of The World</b>,
  //   modal: (
  //     <ImageModal
  //       ctaText="Learn more"
  //       ctaOnClick={() =>
  //         router.push({
  //           pathname: '/heroes',
  //           query: {
  //             ...router.query,
  //           },
  //         })
  //       }
  //     >
  //       <HeroDiscountOffer />
  //     </ImageModal>
  //   ),
  // };

  // Store-specific secondary offers
  switch (store) {
    case ShopifyStore.US:
      return [
        {
          title: (
            <>
              <b>HSA/FSA</b> | Save more with your pre-tax dollars
            </>
          ),
          image: ['web_v2/icons/hsa-fsa/hsa-fsa-icon-black-bg-white.svg'],
          modal: (
            <ImageModal>
              <HsaFsaOffer />
            </ImageModal>
          ),
        },
      ];

    case ShopifyStore.IN:
    case ShopifyStore.GB:
    case ShopifyStore.AE:
    case ShopifyStore.EU:
    case ShopifyStore.ROW:
    default:
      return [];
  }
};

// Get country-specific offers based on store
const getOffersData = (store: ShopifyStore, t: (key: string) => string) => {
  switch (store) {
    case ShopifyStore.IN:
      return [
        {
          title: (
            <>
              {/* <b>No cost EMI</b> starting at{' '} */}
              {t('ring.productSelection.in.offerD.a')}{' '}
              {t('ring.productSelection.in.offerD.b')}{' '}
              <span style={{ whiteSpace: 'nowrap' }}>₹ 1,187/month</span>
            </>
          ),
          image: ['web_v2/icons/primary_offers/0-percent.png'],
        },
        {
          title: (
            <>
              {/* <b>CRED Pay</b> | Earn rewards, pay later options */}
              CRED pay | Save and earn rewards
              {/* {t('ring.productSelection.in.offerB.a')}{' '}
              {t('ring.productSelection.in.offerB.b')} */}
            </>
          ),
          image: ['web_v2/icons/primary_offers/cred.png'],
        },
        {
          title: (
            <>
              {/* Easy payments with <b>UPI and Wallets</b> */}
              Easy payments with UPI
              {/* {t('ring.productSelection.in.offerA.a')}{' '}
              {t('ring.productSelection.in.offerA.b')} */}
            </>
          ),
          image: ['web_v2/icons/primary_offers/upi.png'],
        },
      ];
    case ShopifyStore.US:
      return [
        {
          title: (
            <>
              {/* <b>Shop Pay</b> | Pay in full or in parts */}
              Save more with your pre-tax dollars
            </>
          ),
          // image: ['web_v2/icons/primary_offers/hsa-fsa-pill-3.png'],
          image: ['web_v2/icons/hsa-fsa/hsa-fsa-black-outline.svg'],
        },
        {
          title: (
            <>
              {/* <b>HSA | FSA</b> Powered by Flex */}
              As low as 0 APR financing | Klarna, AfterPay & Affirm
            </>
          ),
          image: [
            'web_v2/icons/primary_offers/afterpay.png',
            'web_v2/icons/primary_offers/klara.png',
            'web_v2/icons/primary_offers/affirmm.png',
          ],
        },
        {
          title: (
            <>
              {/* <b>Flexible payments</b> with <b>Klarna</b> | Pay now, later
                or in interest-free instalments */}
              Shop Pay | Pay in full or in parts
            </>
          ),
          image: ['web_v2/icons/primary_offers/shoppay.png'],
          imageType: 'square',
        },
        {
          title: (
            <>
              {/* Instant payment with <b>PayPal, Apple Pay, or GPay</b> */}
              Instant payment with PayPal, Apple Pay, or GPay
            </>
          ),
          image: [
            'web_v2/icons/primary_offers/paypal.png',
            'web_v2/icons/primary_offers/applepay.png',
            'web_v2/icons/primary_offers/gpay.png',
          ],
        },
      ];
    case ShopifyStore.GB:
      return [
        {
          title: <>One-tap checkout with Shop Pay</>,
          image: ['web_v2/icons/primary_offers/shoppay.png'],
        },
        {
          title: (
            <>
              {/* <b>Flexible payments</b> with <b>Klarna</b> | Pay now, later
                              or in interest-free instalments */}
              Instant payment with PayPal
            </>
          ),
          image: ['web_v2/icons/primary_offers/paypal.png'],
          imageType: 'square',
        },
        {
          title: (
            <>
              {/* <b>Flexible payments</b> with <b>Klarna</b> | Pay now, later
                              or in interest-free instalments */}
              As low as 0 APR financing | Klarna
            </>
          ),
          image: ['web_v2/icons/primary_offers/klara.png'],
          imageType: 'square',
        },
      ];
    case ShopifyStore.AE:
      return [
        {
          title: <>Instant payment with Apple Pay</>,
          image: ['web_v2/icons/primary_offers/applepay.png'],
        },
        {
          title: <>Split payments with Tabby, Tamara or Cashew</>,
          image: [
            'web_v2/icons/primary_offers/icon.png',
            'web_v2/icons/primary_offers/tamara_new.png',
            'web_v2/icons/primary_offers/cashew.png',
          ],
        },
        // {
        //   title: <>Free Cash on Delivery</>,
        //   image: ['web_v2/icons/primary_offers/cod.png'],
        // },
      ];
    case ShopifyStore.EU:
      return [
        {
          title: <>One-tap checkout with Shop Pay</>,
          image: ['web_v2/icons/primary_offers/shoppay.png'],
        },
        {
          title: (
            <>
              {/* <b>Flexible payments</b> with <b>Klarna</b> | Pay now, later
or in interest-free instalments */}
              Instant payment with PayPal
            </>
          ),
          image: ['web_v2/icons/primary_offers/paypal.png'],
          imageType: 'square',
        },
        {
          title: (
            <>
              {/* <b>Flexible payments</b> with <b>Klarna</b> | Pay now, later
or in interest-free instalments */}
              As low as 0 APR financing | Klarna
            </>
          ),
          image: ['web_v2/icons/primary_offers/klara.png'],
          imageType: 'square',
        },
      ];
    default:
      return [
        {
          title: <>Express checkout with PayPal</>,
          image: ['web_v2/icons/primary_offers/paypal.png'],
        },
        {
          title: <>Instant payment with Apple Pay</>,
          image: ['web_v2/icons/primary_offers/applepay.png'],
        },
      ];
  }
};

export const RareBuy = ({
  defaultSize,
  defaultUhx,
  store,
  baseStore,
  currency,
  rareShippingEstimate,
  estimates,
}: {
  defaultSize: RingSizes | null;
  defaultUhx: boolean;
  store: ShopifyStore;
  baseStore?: ShopifyStore;
  currency: Currency;
  rareShippingEstimate?: {
    message: string;
    timeframe: string;
    subtitle: string;
  };
  estimates?: ShippingEstimateObject;
}) => {
  const t = useTranslations('ring');
  const router = useRouter();
  const searchParams = useSearchParams();
  const { regionSlug } = useRegionLocale();
  const locale = searchParams?.get('locale') as string;

  useGlobalUI({ headerCollaspable: true });

  useEffect(() => {
    if (locale && !ALLOWED_RARE_COUNTRIES.includes(locale)) {
      router.replace(`/${regionSlug}/rare`);
    }
  }, [locale, regionSlug, router]);

  return (
    <>
      <PageWrapper>
        <RareSelectionContextProvider
          currency={currency}
          defaultSize={defaultSize}
          defaultColor={RareColors.DESERT_ROSE}
          defaultUhx={defaultUhx}
          rareShippingEstimate={rareShippingEstimate}
          estimates={estimates}
          baseStore={baseStore}
          notServicable={store === ShopifyStore.US}
          notServicableMessage="We're currently unable to sell or ship this product to U.S. addresses. We'll update you here if that changes."
        >
          <RareProductSelectionSection
            dealsBackgroundColor="#F5F1ED"
            offersData={getOffersData(store, t)}
            secondaryOfferData={getSecondaryOfferData(store)}
          />
          <RareSecondaryNavbar />
          <Contents
            htmlId="rare-contents"
            contentData={RARE_CONTENTS_DATA}
            contentsPadding="20px"
            $contentsBackgroundColor="#F5F1ED"
            titleA="What's in"
            titleB="the box"
          />
          <ColorCatalogueSection
            head="Expert craftsmanship."
            descriptionAlignment="center"
            backgroundColor="#111"
          />
          <ExpertSection isPricingPage={true} htmlId="rare-overview" />

          <SolidBgTextSection backgroundColor="transparent" />
          <Capabilities htmlId="rare-capabilities" />
          <TechnicalSpecs
            htmlId="rare-specs"
            title="Technical Specifications"
            imageBackgroundColor="#fdf8f2"
            images={[
              {
                src: 'web_v2/illustrations/spec-a-up.png/w_1920',
                alt: 'Ultrahuman Rare Ring',
                textBold: 'Weighs 5.4gms.',
                textNormal: ' 11x lighter than an average smart watch.',
              },
              {
                src: 'web_v2/illustrations/spec-b-up.png/w_1920',
                alt: 'Ultrahuman Rare Ring Side',
                textBold: '2.45 mm thickness.',
                textNormal: ' Masterfully sleek, undeniably minimal.',
              },
            ]}
            accordionItems={[
              {
                title: <span className="bold">Body</span>,
                content: (
                  <>
                    <div className="content">
                      <span className="bold">Dimensions</span>
                      <span>Width - 8mm</span>
                      <span>Thickness - 2.45mm</span>
                    </div>
                    <div className="content">
                      <span className="bold">Weight</span>
                      <span>4 - 8 g (varies with size)</span>
                    </div>
                  </>
                ),
              },
              {
                title: <span className="bold">Materials</span>,
                content: (
                  <div className="content">
                    <span>18K Gold</span>
                    <span>Pt950 Platinum</span>
                    <span>Water resistant up to 100m/328 ft</span>
                    <span>
                      Non-allergenic, non-metallic, seamless hypoallergenic
                      resin
                    </span>
                  </div>
                ),
              },
              {
                title: <span className="bold">Connectivity</span>,
                content: (
                  <div className="content">
                    <span>EMF-safe</span>
                    <span>Bluetooth low energy</span>
                    <span>Automatic firmware updates via UH app</span>
                  </div>
                ),
              },
              {
                title: <span className="bold">Battery & Power</span>,
                content: (
                  <div className="content">
                    <span>Up to 6 days of battery life</span>
                  </div>
                ),
              },
              {
                title: <span className="bold">Sensors</span>,
                content: (
                  <div className="content">
                    <span>6DOF Motion Sensor</span>
                    <span>
                      Photoplethysmography (PPG) sensor (For HR and blood
                      oxygen)
                    </span>
                    <span>Skin temperature sensor (Far infrared sensor)</span>
                  </div>
                ),
              },
              {
                title: <span className="bold">Other Key Specifications</span>,
                content: (
                  <div className="content">
                    <span>Operating temp: -5 to 50°C</span>{' '}
                    <span>BLE transmission power: 0 dBM(1mw)</span>
                    <span>BLE frequency: 2402 to 2480MHz</span>
                  </div>
                ),
              },
            ]}
          />

          <ProductWizard
            title={
              'Still unsure if the Rare Ring is the right wearable for you?'
            }
            backgroundColor="#FDF8F2"
          />
          <FAQSection faqData={faqData} htmlId="rare-faq" />
          <BottomBar />
        </RareSelectionContextProvider>
      </PageWrapper>
    </>
  );
};
